import { Subsystem, PlacedBlock, SocketConnection, IsoPoint, PlacedLabel, TextAnnotation } from '@/app/types/cad';
import { isoToScreen } from '@/app/utils/isometric';
import { transformPoint, getTransformedLineEndpoints } from '@/app/utils/transform';

/**
 * Generates a preview image for a subsystem
 * @param subsystem - The subsystem to generate preview for
 * @param width - Preview width in pixels
 * @param height - Preview height in pixels
 * @returns Data URL of the preview image
 */
export function generateSubsystemPreview(
  subsystem: Subsystem,
  width: number = 200,
  height: number = 200
): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  // Validate subsystem has elements array
  if (!subsystem.elements || !Array.isArray(subsystem.elements)) {
    console.warn('⚠️ Subsystem has no elements array in generateSubsystemPreview:', subsystem);
    return canvas.toDataURL(); // Return empty canvas
  }

  // Clear with transparent background
  ctx.clearRect(0, 0, width, height);

  // Calculate bounds of all elements
  const bounds = calculateSubsystemBounds(subsystem);
  
  if (!bounds) {
    // No elements to render
    return canvas.toDataURL();
  }

  // Calculate scale to fit in canvas with padding
  const padding = 20;
  const availableWidth = width - 2 * padding;
  const availableHeight = height - 2 * padding;
  
  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;
  
  const scale = Math.min(
    availableWidth / Math.max(boundsWidth, 1),
    availableHeight / Math.max(boundsHeight, 1),
    2 // Max scale to avoid tiny subsystems being too large
  );

  // Center offset
  const offsetX = width / 2 - (bounds.minX + boundsWidth / 2) * scale;
  const offsetY = height / 2 - (bounds.minY + boundsHeight / 2) * scale;

  // Transform function for preview space
  const toPreviewSpace = (screenPoint: Point): Point => ({
    x: screenPoint.x * scale + offsetX,
    y: screenPoint.y * scale + offsetY,
  });

  // Draw blocks
  subsystem.elements.forEach(element => {
    if (element.type === 'block' && element.data) {
      const block = element.data as PlacedBlock;
      drawBlockPreview(ctx, block, toPreviewSpace);
    }
  });

  // Draw connections
  subsystem.elements.forEach(element => {
    if (element.type === 'connection' && element.data) {
      const conn = element.data as SocketConnection;
      drawConnectionPreview(ctx, conn, toPreviewSpace);
    }
  });

  // Draw labels
  subsystem.elements.forEach(element => {
    if (element.type === 'label' && element.data) {
      const label = element.data as PlacedLabel;
      drawLabelPreview(ctx, label, toPreviewSpace, scale);
    }
  });

  // Draw text annotations
  subsystem.elements.forEach(element => {
    if (element.type === 'text-annotation' && element.data) {
      const text = element.data as TextAnnotation;
      drawTextPreview(ctx, text, toPreviewSpace, scale);
    }
  });

  return canvas.toDataURL();
}

interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

type Point = { x: number; y: number };

function calculateSubsystemBounds(subsystem: Subsystem): Bounds | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let hasElements = false;

  if (!subsystem.elements || !Array.isArray(subsystem.elements)) {
    console.warn('⚠️ Subsystem has no elements array:', subsystem);
    return null;
  }

  subsystem.elements.forEach(element => {
    if (element.type === 'block' && element.data) {
      const block = element.data as PlacedBlock;
      const blockData = block.blockData;

      if (!blockData) {
        console.warn('⚠️ Block without blockData:', block.id);
        return;
      }

      if (!blockData.elements || !Array.isArray(blockData.elements)) {
        console.warn('⚠️ Block blockData.elements is not an array:', block.id, typeof blockData.elements);
        return;
      }

      // Get all points from block elements
      const points: IsoPoint[] = [];
      
      // Process drawing elements
      blockData.elements.forEach(el => {
        if (el.type === 'line') {
          if (el.start && el.end) {
            points.push(el.start, el.end);
          }
        } else if (el.type === 'connection' && el.position) {
          points.push(el.position);
        } else if (el.type === 'circle' && el.center) {
          // Add circle bounds by sampling points around the circle
          const radius = el.radius || 0;
          for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 4) {
            const offsetU = radius * Math.cos(angle);
            const offsetV = radius * Math.sin(angle);
            points.push({
              u: el.center.u + offsetU,
              v: el.center.v + offsetV
            });
          }
        } else if (el.type === 'arc' && el.center) {
          // Add arc bounds by sampling points along the arc
          const radius = el.radius || 0;
          let startAngle = el.startAngle;
          let endAngle = el.endAngle;
          
          if (el.counterClockwise) {
            [startAngle, endAngle] = [endAngle, startAngle];
          }
          
          if (endAngle < startAngle) {
            endAngle += Math.PI * 2;
          }
          
          const numSamples = 8;
          for (let i = 0; i <= numSamples; i++) {
            const t = i / numSamples;
            const angle = startAngle + t * (endAngle - startAngle);
            const offsetU = radius * Math.cos(angle);
            const offsetV = radius * Math.sin(angle);
            points.push({
              u: el.center.u + offsetU,
              v: el.center.v + offsetV
            });
          }
        } else if (el.type === 'shape' && el.points && Array.isArray(el.points)) {
          points.push(...el.points);
        } else if (el.type === 'spline' && el.points && Array.isArray(el.points)) {
          points.push(...el.points);
        }
      });

      // Transform and convert to screen space
      points.forEach(p => {
        const transformed = transformPoint(p, block.transform);
        const screen = isoToScreen(transformed);
        minX = Math.min(minX, screen.x);
        maxX = Math.max(maxX, screen.x);
        minY = Math.min(minY, screen.y);
        maxY = Math.max(maxY, screen.y);
        hasElements = true;
      });
    } else if (element.type === 'connection' && element.data) {
      const conn = element.data as SocketConnection;
      if (conn.position) {
        const screen = isoToScreen(conn.position);
        minX = Math.min(minX, screen.x);
        maxX = Math.max(maxX, screen.x);
        minY = Math.min(minY, screen.y);
        maxY = Math.max(maxY, screen.y);
        hasElements = true;
      }
    } else if (element.type === 'label' && element.data) {
      const label = element.data as PlacedLabel;
      if (label.position) {
        const screen = isoToScreen(label.position);
        minX = Math.min(minX, screen.x);
        maxX = Math.max(maxX, screen.x);
        minY = Math.min(minY, screen.y);
        maxY = Math.max(maxY, screen.y);
        hasElements = true;
      }
    } else if (element.type === 'text-annotation' && element.data) {
      const text = element.data as TextAnnotation;
      if (text.position) {
        const screen = isoToScreen(text.position);
        minX = Math.min(minX, screen.x);
        maxX = Math.max(maxX, screen.x);
        minY = Math.min(minY, screen.y);
        maxY = Math.max(maxY, screen.y);
        hasElements = true;
      }
    }
  });

  if (!hasElements) {
    return null;
  }

  // Add some padding to bounds
  const padding = 10;
  return {
    minX: minX - padding,
    maxX: maxX + padding,
    minY: minY - padding,
    maxY: maxY + padding,
  };
}

function drawBlockPreview(
  ctx: CanvasRenderingContext2D,
  block: PlacedBlock,
  toPreviewSpace: (p: Point) => Point
) {
  const blockData = block.blockData;
  
  if (!blockData) {
    console.warn('⚠️ Block without blockData in drawBlockPreview:', block.id);
    return;
  }

  if (!blockData.elements || !Array.isArray(blockData.elements)) {
    console.warn('⚠️ Block blockData.elements is not an array in drawBlockPreview:', block.id, typeof blockData.elements);
    return;
  }

  const color = block.colorOverride || '#000000';

  // Draw all drawing elements
  blockData.elements.forEach(el => {
    if (el.type === 'line') {
      if (!el.start || !el.end) {
        return;
      }
      
      const { start, end } = getTransformedLineEndpoints(el, block.transform);
      const screenStart = isoToScreen(start);
      const screenEnd = isoToScreen(end);
      
      const previewStart = toPreviewSpace(screenStart);
      const previewEnd = toPreviewSpace(screenEnd);

      ctx.strokeStyle = el.color || color;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(previewStart.x, previewStart.y);
      ctx.lineTo(previewEnd.x, previewEnd.y);
      ctx.stroke();
    } else if (el.type === 'connection' && el.position) {
      // Draw connection points as small filled circles
      const fillColor = el.color || color;
      
      const transformed = transformPoint(el.position, block.transform);
      const screen = isoToScreen(transformed);
      const preview = toPreviewSpace(screen);

      ctx.fillStyle = el.fillType === 'filled' ? fillColor : '#ffffff';
      ctx.strokeStyle = fillColor;
      ctx.lineWidth = 1.5;

      ctx.beginPath();
      ctx.arc(preview.x, preview.y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    } else if (el.type === 'circle' && el.center) {
      // Draw circles as ellipses in isometric projection
      drawIsometricCircle(ctx, el, block.transform, toPreviewSpace, color);
    } else if (el.type === 'arc' && el.center) {
      // Draw arcs as partial ellipses in isometric projection
      drawIsometricArc(ctx, el, block.transform, toPreviewSpace, color);
    } else if (el.type === 'shape' && el.points && el.points.length > 0) {
      // Draw polygon
      ctx.strokeStyle = el.strokeColor || color;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      
      el.points.forEach((point, idx) => {
        const transformed = transformPoint(point, block.transform);
        const screen = isoToScreen(transformed);
        const preview = toPreviewSpace(screen);
        
        if (idx === 0) {
          ctx.moveTo(preview.x, preview.y);
        } else {
          ctx.lineTo(preview.x, preview.y);
        }
      });
      
      if (el.closed) {
        ctx.closePath();
      }
      
      ctx.stroke();
      
      if (el.filled) {
        ctx.fillStyle = el.fillColor || color;
        ctx.fill();
      }
    } else if (el.type === 'spline' && el.points && el.points.length > 0) {
      // Draw spline as smooth curve
      ctx.strokeStyle = el.color || color;
      ctx.lineWidth = 1.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      
      el.points.forEach((point, idx) => {
        const transformed = transformPoint(point, block.transform);
        const screen = isoToScreen(transformed);
        const preview = toPreviewSpace(screen);
        
        if (idx === 0) {
          ctx.moveTo(preview.x, preview.y);
        } else {
          ctx.lineTo(preview.x, preview.y);
        }
      });
      
      ctx.stroke();
    }
  });
}

function drawConnectionPreview(
  ctx: CanvasRenderingContext2D,
  conn: SocketConnection,
  toPreviewSpace: (p: Point) => Point
) {
  const screen = isoToScreen(conn.position);
  const preview = toPreviewSpace(screen);

  // Draw connection as a highlighted dot
  ctx.fillStyle = conn.color || '#FF6B00';
  ctx.beginPath();
  ctx.arc(preview.x, preview.y, 3, 0, Math.PI * 2);
  ctx.fill();

  // Draw outer ring
  ctx.strokeStyle = conn.color || '#FF6B00';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(preview.x, preview.y, 5, 0, Math.PI * 2);
  ctx.stroke();
}

function drawLabelPreview(
  ctx: CanvasRenderingContext2D,
  label: PlacedLabel,
  toPreviewSpace: (p: Point) => Point,
  scale: number
) {
  const screen = isoToScreen(label.position);
  const preview = toPreviewSpace(screen);

  ctx.font = `${scale * 10}px Arial`;
  ctx.fillStyle = label.color || '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillText(label.text, preview.x, preview.y);
}

function drawTextPreview(
  ctx: CanvasRenderingContext2D,
  text: TextAnnotation,
  toPreviewSpace: (p: Point) => Point,
  scale: number
) {
  const screen = isoToScreen(text.position);
  const preview = toPreviewSpace(screen);

  ctx.font = `${scale * 10}px Arial`;
  ctx.fillStyle = text.color || '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillText(text.text, preview.x, preview.y);
}

/**
 * Regenerate preview for existing subsystem
 */
export function updateSubsystemPreview(subsystem: Subsystem): Subsystem {
  const preview = generateSubsystemPreview(subsystem);
  return {
    ...subsystem,
    preview,
    modified: new Date().toISOString(),
  };
}

/**
 * Draw a circle in screen space (not isometric projection)
 */
function drawIsometricCircle(
  ctx: CanvasRenderingContext2D,
  el: any,
  transform: any,
  toPreviewSpace: (p: Point) => Point,
  color: string
) {
  // Transform center to screen space
  const transformed = transformPoint(el.center, transform);
  const screen = isoToScreen(transformed);
  const preview = toPreviewSpace(screen);
  
  // Calculate radius in screen pixels (approximate scaling)
  // Use a simple scaling factor based on the preview transform
  const radiusInScreenSpace = el.radius * 50; // Approximate pixel scaling
  
  ctx.strokeStyle = el.color || color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  
  if (el.filled) {
    ctx.fillStyle = el.fillColor || el.color || color;
  }

  ctx.beginPath();
  ctx.arc(preview.x, preview.y, radiusInScreenSpace, 0, 2 * Math.PI);
  
  if (el.filled) {
    ctx.fill();
  }
  ctx.stroke();
}

/**
 * Draw an arc in screen space (not isometric projection)
 */
function drawIsometricArc(
  ctx: CanvasRenderingContext2D,
  el: any,
  transform: any,
  toPreviewSpace: (p: Point) => Point,
  color: string
) {
  // Transform center to screen space
  const transformed = transformPoint(el.center, transform);
  const screen = isoToScreen(transformed);
  const preview = toPreviewSpace(screen);
  
  // Calculate radius in screen pixels
  const radiusInScreenSpace = el.radius * 50; // Approximate pixel scaling
  
  let startAngle = el.startAngle;
  let endAngle = el.endAngle;
  let counterClockwise = el.counterClockwise || false;
  
  ctx.strokeStyle = el.color || color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';

  ctx.beginPath();
  ctx.arc(preview.x, preview.y, radiusInScreenSpace, startAngle, endAngle, counterClockwise);
  
  ctx.stroke();
}